import { CaretRight, Check, type Icon } from "@phosphor-icons/react";
import type { ReactNode } from "react";
import {
  Menu as AriaMenu,
  MenuItem as AriaMenuItem,
  type MenuItemProps as AriaMenuItemProps,
  type MenuProps as AriaMenuProps,
  MenuSection as AriaMenuSection,
  Header,
  MenuTrigger,
  Separator,
  SubmenuTrigger,
  Text,
} from "react-aria-components";
import { cx } from "@/lib/cx";
import { Kbd } from "./Kbd";
import { listPad, optionRow, sectionHeader } from "./list-styles";
import { Popover } from "./Popover";

export { MenuTrigger, SubmenuTrigger };

/**
 *   <MenuTrigger>
 *     <IconButton aria-label="More" icon={DotsThree} />
 *     <Menu onAction={...}><MenuItem id="x" icon={Gear}>Settings</MenuItem></Menu>
 *   </MenuTrigger>
 */
export function Menu<T extends object>({
  className,
  placement = "bottom start",
  header,
  ...props
}: AriaMenuProps<T> & {
  placement?: "bottom start" | "bottom end" | "top start" | "top end" | "right top";
  /** Static, non-interactive content above the items (e.g. the signed-in user in the account menu). */
  header?: ReactNode;
}) {
  return (
    <Popover placement={placement} className={cx("max-w-80", header ? "min-w-60" : "min-w-48")}>
      {header}
      <AriaMenu className={cx(listPad, className as string)} {...props} />
    </Popover>
  );
}

export interface MenuItemProps extends Omit<AriaMenuItemProps, "className" | "children"> {
  icon?: Icon;
  /** Shortcut hint shown on the right, e.g. ["G", "I"]. */
  shortcut?: string[];
  tone?: "default" | "danger";
  children: ReactNode;
}

/**
 * Menu row: optional icon, label, optional shortcut. In a menu with `selectionMode`, the selected
 * row shows a check; a row that opens a submenu (inside SubmenuTrigger) shows a caret.
 */
export function MenuItem({ icon: I, shortcut, tone = "default", children, textValue, ...props }: MenuItemProps) {
  return (
    <AriaMenuItem
      textValue={textValue ?? (typeof children === "string" ? children : undefined)}
      data-ring="inset"
      className={cx(optionRow, "group/item", tone === "danger" && "text-danger-text")}
      {...props}
    >
      {({ isSelected, hasSubmenu, selectionMode }) => (
        <>
          {I ? (
            <I
              size={16}
              aria-hidden="true"
              className={cx("shrink-0", tone === "danger" ? "" : "text-fg-muted group-data-focused/item:text-fg")}
            />
          ) : null}
          <Text slot="label" className="min-w-0 flex-1 truncate-title">
            {children}
          </Text>
          {shortcut ? <Kbd keys={shortcut} className="ml-3" /> : null}
          {selectionMode !== "none" ? (
            <Check
              size={14}
              aria-hidden="true"
              className={cx("ml-2 shrink-0 text-accent-text", isSelected ? "opacity-100" : "opacity-0")}
            />
          ) : null}
          {hasSubmenu ? <CaretRight size={12} aria-hidden="true" className="ml-2 shrink-0 text-fg-subtle" /> : null}
        </>
      )}
    </AriaMenuItem>
  );
}

export function MenuSeparator() {
  return <Separator className="-mx-1 my-1 h-px bg-line" />;
}

export function MenuSection({ title, children }: { title?: string; children: ReactNode }) {
  return (
    <AriaMenuSection>
      {title ? <Header className={sectionHeader}>{title}</Header> : null}
      {children}
    </AriaMenuSection>
  );
}
